import type { FC } from 'react'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { UserMenu } from '@/components/generals/user-menu'
import { useSite } from '@/components/generals/site-context'
import { Button, ButtonLink } from '@/components/ui/button'

import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

const NAV = [
    { href: '/jobs', label: 'Jobs' },
    { href: '/companies', label: 'Companies' },
    { href: '/resumes', label: 'Resume builder' },
    { href: '/matches', label: 'Matches' }
]

export const Header: FC = () => {
    const { viewer } = useSite()
    const router = useRouter()
    const [open, setOpen] = useState(false)

    const isActive = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <header className='sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur supports-backdrop-filter:bg-background/75'>
            <div className='container-page flex h-16 items-center gap-6'>
                <Logo />

                <nav aria-label='Main' className='hidden items-center gap-1 md:flex'>
                    {NAV.map(item => (
                        <Link
                            key={item.href}
                            href={item.href}
                            aria-current={isActive(item.href) ? 'page' : undefined}
                            className={cn(
                                'rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                                isActive(item.href) && 'text-foreground'
                            )}
                        >
                            {item.label}
                        </Link>
                    ))}
                </nav>

                <div className='ml-auto flex items-center gap-2'>
                    {viewer ? (
                        <>
                            <ButtonLink href='/dashboard' variant='ghost' className='hidden sm:inline-flex'>
                                Dashboard
                            </ButtonLink>
                            <UserMenu viewer={viewer} />
                        </>
                    ) : (
                        <>
                            <ButtonLink href='/login' variant='ghost' className='hidden sm:inline-flex'>
                                Sign in
                            </ButtonLink>
                            <ButtonLink href='/signup'>
                                Get started
                            </ButtonLink>
                        </>
                    )}

                    <Button
                        variant='ghost'
                        size='icon'
                        className='md:hidden'
                        aria-label='Open menu'
                        onClick={() => setOpen(true)}
                    >
                        <Menu />
                    </Button>
                </div>
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side='right'>
                    <SheetHeader>
                        <SheetTitle>Menu</SheetTitle>
                    </SheetHeader>
                    <nav aria-label='Mobile' className='flex flex-col gap-1 px-3 pb-6'>
                        {NAV.map(item => (
                            <Link
                                key={item.href}
                                href={item.href}
                                onClick={() => setOpen(false)}
                                className='rounded-lg px-3 py-3 text-base font-medium hover:bg-muted'
                            >
                                {item.label}
                            </Link>
                        ))}
                        <div className='my-2 h-px bg-border' />
                        {viewer ? (
                            <Link href='/dashboard' onClick={() => setOpen(false)} className='rounded-lg px-3 py-3 text-base font-medium hover:bg-muted'>
                                Dashboard
                            </Link>
                        ) : (
                            <>
                                <Link href='/login' onClick={() => setOpen(false)} className='rounded-lg px-3 py-3 text-base font-medium hover:bg-muted'>
                                    Sign in
                                </Link>
                                <Link href='/signup' onClick={() => setOpen(false)} className='rounded-lg px-3 py-3 text-base font-medium text-primary hover:bg-muted'>
                                    Create a free account
                                </Link>
                            </>
                        )}
                    </nav>
                </SheetContent>
            </Sheet>
        </header>
    )
}
